import { type ChangeEvent, type FC, useCallback, useEffect, useState } from 'react';
import { Box, Checkbox, Flex, FormLabel } from '@chakra-ui/react';
import _debounce from 'lodash-es/debounce';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Header, Rating, Text } from '../../../ui-components';
import { Field } from '../../field';
import { filtersMap } from '../const';
import { TurnToSelect } from './Select';
import { TurnToTag } from './Tag';
import type { ReviewFilters, SortOptions } from '../types';

export interface FiltersProps {
    onFilter: (value: ReviewFilters | null) => void;
    onSearch: (value: string) => void;
    onSort: (value: SortOptions) => void;
    sortOption: SortOptions;
    ratingBreakdown: {
        [key: string]: number;
    };
    filtersList: ReviewFilters[];
    totalNumberReviews: number;

    removeFilter?: (filterName: ReviewFilters) => void;
    clearFilters?: () => void;
}
export const Filters: FC<FiltersProps> = ({
    onSort,
    sortOption,
    onSearch,
    ratingBreakdown,
    onFilter,
    filtersList,
    removeFilter,
    totalNumberReviews,
    clearFilters,
}) => {
    const filterOptions = [
        {
            value: '1',
            label: <Text size="small">({ratingBreakdown['1']})</Text>,
            icon: (
                <Flex alignItems="center" gap={2} marginRight={2}>
                    <Checkbox isChecked={filtersList.includes('1')} />
                    <Rating initialRating={1} iconSize="sm" />
                </Flex>
            ),
        },
        {
            value: '2',
            label: <Text size="small">({ratingBreakdown['2']})</Text>,
            icon: (
                <Flex alignItems="center" gap={2} marginRight={2}>
                    <Checkbox isChecked={filtersList.includes('2')} />
                    <Rating initialRating={2} iconSize="sm" />
                </Flex>
            ),
        },
        {
            value: '3',
            label: <Text size="small">({ratingBreakdown['3']})</Text>,
            icon: (
                <Flex alignItems="center" gap={2} marginRight={2}>
                    <Checkbox isChecked={filtersList.includes('3')} />
                    <Rating initialRating={3} iconSize="sm" />
                </Flex>
            ),
        },
        {
            value: '4',
            label: <Text size="small">({ratingBreakdown['4']})</Text>,
            icon: (
                <Flex alignItems="center" gap={2} marginRight={2}>
                    <Checkbox isChecked={filtersList.includes('4')} />
                    <Rating initialRating={4} iconSize="sm" />
                </Flex>
            ),
        },
        {
            value: '5',
            label: <Text size="small">({ratingBreakdown['5']})</Text>,
            icon: (
                <Flex alignItems="center" gap={2} marginRight={2}>
                    <Checkbox isChecked={filtersList.includes('5')} />
                    <Rating initialRating={5} iconSize="sm" />
                </Flex>
            ),
        },
    ];

    const sortOptions = [
        { value: 'mostHelpful', label: 'Most helpful' },
        { value: 'mostRecent', label: 'Most recent' },
        { value: 'oldest', label: 'Oldest' },
        { value: 'highestRated', label: 'Highest Rated' },
        { value: 'lowestRated', label: 'Lowest Rated' },
    ];
    const intl = useIntl();
    const form = useForm();
    const [searchValue, setSearchValue] = useState<string>('');

    const debounceSearch = useCallback(
        _debounce((value: string) => onSearch(value), 500),
        [],
    );

    useEffect(() => {
        debounceSearch(searchValue);
    }, [searchValue, debounceSearch]);

    return (
        <Box mt={8} borderBottomWidth="1px" borderColor="slt-pepper.300">
            <Header as="h4" variant="header4" style="bold">
                <FormattedMessage
                    id="turn-to.filters.total-reviews"
                    defaultMessage="1-{end} of {total} Reviews"
                    values={{
                        end: Math.min(5, totalNumberReviews),
                        total: totalNumberReviews,
                    }}
                />
            </Header>
            <Flex
                mt={4}
                flexWrap={{ base: 'wrap', lg: 'nowrap' }}
                gap={4}
                flex={1}
                justifyContent="space-between"
            >
                <Box w={{ base: '100%', lg: 'auto' }} minW="343px">
                    <FormLabel
                        htmlFor="reviewSort"
                        data-testid="reviewSort-label"
                        variant="new-style"
                    >
                        <Text style="bold">
                            {intl.formatMessage({
                                id: 'turn-to.filters.sort',
                                defaultMessage: 'Sort',
                            })}
                        </Text>
                    </FormLabel>
                    <TurnToSelect
                        id="reviewSort"
                        name="reviewSort"
                        options={sortOptions}
                        onChange={(selectedOption) => onSort(selectedOption.value as SortOptions)}
                        value={sortOptions.find((opt) => opt.value === sortOption)}
                        isDisabled={searchValue.length > 0}
                    />
                </Box>
                <Flex
                    w={{ base: '100%', lg: 'auto' }}
                    gap={4}
                    flexWrap={{ base: 'wrap', lg: 'nowrap' }}
                >
                    <Box minW="343px" w={{ base: '100%', lg: 'auto' }}>
                        <FormLabel
                            htmlFor="reviewFilter"
                            data-testid="reviewFilter-label"
                            variant="new-style"
                        >
                            <Text style="bold">
                                {intl.formatMessage({
                                    id: 'turn-to.filters.filter',
                                    defaultMessage: 'Filter',
                                })}
                            </Text>
                        </FormLabel>
                        <TurnToSelect
                            id="reviewFilter"
                            name="reviewFilter"
                            options={filterOptions}
                            onChange={(selectedOption) =>
                                onFilter((selectedOption?.value as ReviewFilters) || null)
                            }
                            value={{ value: 'select', label: 'Star ratings', icon: null }}
                            closeMenuOnSelect={false}
                            isDisabled={searchValue.length > 0}
                        />
                    </Box>
                    <Box minW="343px" w={{ base: '100%', lg: 'auto' }}>
                        <Field
                            name="reviewsSearch"
                            label={
                                <Text style="bold">
                                    {intl.formatMessage({
                                        id: 'turn-to.filters.search',
                                        defaultMessage: 'Search',
                                    })}
                                </Text>
                            }
                            placeholder={intl.formatMessage({
                                id: 'turn-to.filters.search-reviews',
                                defaultMessage: 'Search reviews',
                            })}
                            rightIcon="search"
                            control={form.control}
                            type="text"
                            id="reviewsSearch"
                            variant="new-style"
                            inputProps={() => ({
                                paddingRight: '32px',
                                borderRadius: 0,
                                onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                    setSearchValue(e.target.value),
                                value: searchValue,
                            })}
                        />
                    </Box>
                </Flex>
            </Flex>
            <Flex gap={2} my={4}>
                {!!filtersList.length && (
                    <Button variant="text-button-no-hover" onClick={clearFilters}>
                        <FormattedMessage
                            defaultMessage="Clear all"
                            id="turn-to.filter.clear-all"
                        />
                    </Button>
                )}

                {filtersList.map((filter) => (
                    <TurnToTag
                        key={filter}
                        tagText={filtersMap[filter]}
                        onRemove={(value) => removeFilter?.(value as ReviewFilters)}
                        id={filter}
                    />
                ))}
            </Flex>
        </Box>
    );
};
